import { useNavigate } from "react-router-dom";
import { getSchema } from "../data/schemaData";
import {
  ArrowLeft, Lock, ArrowRight, CheckCircle,
  Dumbbell, Clock, Info, Target
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fitsmith-academy/artifacts/zbh0gz3y_291E0B11-36FD-4420-BAF1-DDEC0FEFC105.png";
const DEMO_KEY = "vetverlies-beginner-3x";
const VISIBLE_COUNT = 3; // hoeveel oefeningen volledig zichtbaar zijn

// ─── EXERCISE ROW (demo) ──────────────────────────────────────────────────────
function DemoExerciseRow({ exercise, blurred }) {
  const isSuperset = !!exercise.superset;
  return (
    <div
      className={`schema-exercise-row ${isSuperset ? "schema-superset" : ""} ${blurred ? "demo-blurred" : ""}`}
      style={blurred ? { filter: "blur(5px)", userSelect: "none", pointerEvents: "none" } : {}}
    >
      <div className="schema-ex-code">{blurred ? "??" : exercise.code}</div>
      <div className="schema-ex-info">
        <p className="schema-ex-naam">{blurred ? "████████████████████" : exercise.naam}</p>
        {!blurred && exercise.notitie && (
          <p className="schema-ex-notitie">
            <Info size={10} className="inline mr-1 shrink-0" style={{ verticalAlign: "text-top" }} />
            {exercise.notitie}
          </p>
        )}
        {blurred && <p className="schema-ex-notitie">████████████████ ██████████</p>}
        {isSuperset && !blurred && (
          <span className="schema-superset-label">Superset met {exercise.superset}</span>
        )}
      </div>
      <div className="schema-ex-stats">
        <div className="schema-stat-item">
          <span className="schema-stat-label">Sets/Reps</span>
          <span className="schema-stat-val">{blurred ? "? x ?" : exercise.sets_reps}</span>
        </div>
        <div className="schema-stat-item">
          <span className="schema-stat-label">Tempo</span>
          <span className="schema-stat-val font-mono">{blurred ? "????" : exercise.tempo}</span>
        </div>
        <div className="schema-stat-item">
          <span className="schema-stat-label">Rust</span>
          <span className="schema-stat-val">{blurred ? "?? sec" : exercise.rust}</span>
        </div>
      </div>
    </div>
  );
}

// ─── SCHEMA DEMO ──────────────────────────────────────────────────────────────
export default function SchemaDemo() {
  const navigate = useNavigate();
  const schema = getSchema(DEMO_KEY);
  const session = schema?.sessions[0]; // Sessie A

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Navbar */}
      <nav className="bg-[#0a0a0a] border-b border-white/5 px-4 sm:px-8 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate("/#aanbod")}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft size={16} />
          <span className="text-sm">Terug</span>
        </button>
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="FitSmith" className="h-7 w-7 object-contain" />
          <span className="text-white font-bold text-base tracking-wide">FitSmith</span>
        </div>
        <span className="demo-nav-badge">Demo</span>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="schema-niveau-badge" style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", color: "#4ade80" }}>
              Beginner
            </span>
            <span className="schema-niveau-badge" style={{ background: "rgba(201,168,76,0.1)", border: "1px solid rgba(201,168,76,0.25)", color: "#C9A84C" }}>
              <Target size={10} className="inline mr-1" />
              Vetverlies
            </span>
            <span className="schema-niveau-badge" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", color: "#a1a1aa" }}>
              <Clock size={10} className="inline mr-1" />
              3x per week
            </span>
            <span className="demo-nav-badge ml-1">Voorbeeldschema</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">
            Vetverlies Beginner – 3x per week
          </h1>
          <p className="text-zinc-500 text-sm">Vetverlies met behoud van spiermassa — dit is een gratis preview</p>
        </div>

        {/* Weekstructuur */}
        <div className="schema-week-card mb-8">
          <p className="text-xs font-bold uppercase tracking-widest text-[#C9A84C] mb-2">Weekstructuur</p>
          <p className="text-white text-sm font-semibold whitespace-pre-line">
            Week 1:  Maandag A  |  Woensdag B  |  Vrijdag A*{"\n"}
            Week 2:  Maandag B  |  Woensdag A  |  Vrijdag B*
          </p>
        </div>

        {/* Sessie A — deels zichtbaar */}
        <div className="schema-session-block mb-4">
          <div className="schema-session-header">
            <div className="schema-session-num">A</div>
            <h2 className="schema-session-title">Sessie A</h2>
            <span className="schema-session-count">{session?.exercises?.length} oefeningen</span>
          </div>

          <div className="schema-exercises">
            {/* Kolomkoppen */}
            <div className="schema-col-headers">
              <div className="schema-col-code">Code</div>
              <div className="schema-col-info">Oefening</div>
              <div className="schema-col-stats">Sets / Reps / Tempo / Rust</div>
            </div>

            {session?.exercises?.map((ex, i) => (
              <DemoExerciseRow key={ex.code} exercise={ex} blurred={i >= VISIBLE_COUNT} />
            ))}
          </div>

          {/* Gradient overlay + CTA */}
          <div className="demo-lock-overlay">
            <div className="demo-lock-inner">
              <div className="demo-lock-icon">
                <Lock size={22} className="text-black" />
              </div>
              <p className="text-white font-bold text-base mb-1">
                {(session?.exercises?.length || 0) - VISIBLE_COUNT} oefeningen verborgen
              </p>
              <p className="text-zinc-400 text-sm mb-4">
                Koop het schema om alle oefeningen, Sessie B, weekprogramma en uitvoeringstips te ontvangen.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => { navigate("/"); setTimeout(() => document.querySelector("#aanbod")?.scrollIntoView({ behavior: "smooth" }), 300); }}
                  data-testid="demo-buy-btn"
                  className="btn-gold px-6 py-3 font-bold text-sm uppercase tracking-wide"
                >
                  Schema kopen <ArrowRight size={14} className="inline ml-1" />
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="btn-dark px-6 py-3 font-bold text-sm"
                >
                  Meer informatie
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sessie B — volledig vergrendeld */}
        <div className="demo-session-locked">
          <Lock size={16} className="text-zinc-700" />
          <span className="text-zinc-700 font-semibold text-sm">Sessie B — beschikbaar na aankoop</span>
        </div>
        <div className="demo-session-locked mt-2">
          <Lock size={16} className="text-zinc-700" />
          <span className="text-zinc-700 font-semibold text-sm">Sessie A – Variant — beschikbaar na aankoop</span>
        </div>

        {/* Voordelen + CTA onderaan */}
        <div className="demo-bottom-cta mt-10">
          <div className="mb-6">
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-600 mb-4">Inbegrepen in het volledige schema</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                "Sessie A + Sessie B volledig uitgeschreven",
                "Sessie A* en B* varianten (3x/week rotatie)",
                "Alle sets, reps, tempo en rusttijden",
                "Persoonlijke uitvoeringstips per oefening",
                "Supersets duidelijk gemarkeerd",
                "Weekplanning en trainingsnotities",
              ].map((f) => (
                <div key={f} className="flex items-start gap-2 text-sm text-zinc-400">
                  <CheckCircle size={14} className="text-[#C9A84C] shrink-0 mt-0.5" />
                  {f}
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <p className="text-2xl font-bold text-white mb-1">Vanaf €19 <span className="text-zinc-600 text-base font-normal">eenmalig</span></p>
            <p className="text-zinc-600 text-xs mb-4">Beginner 2x/week · Gevorderd · Advanced · alle frequenties beschikbaar</p>
            <button
              onClick={() => { navigate("/"); setTimeout(() => document.querySelector("#aanbod")?.scrollIntoView({ behavior: "smooth" }), 300); }}
              data-testid="demo-bottom-buy-btn"
              className="btn-gold px-8 py-3.5 font-bold text-sm uppercase tracking-wide"
            >
              Kies jouw schema <ArrowRight size={14} className="inline ml-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
